import { useState, useEffect, useRef } from 'react';

const API = import.meta.env.VITE_API_URL || '/api';

interface Customer {
  id: string;
  name: string;
  taxId?: string;
  email?: string;
  phone?: string;
  type?: 'individual' | 'business';
  address?: string;
  creditLimit?: number | string;
  creditEnabled?: boolean;
  creditDays?: number;
  active?: boolean;
}

export function Customers() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    taxId: '',
    email: '',
    phone: '',
    type: 'individual',
    address: '',
    creditLimit: '',
    creditEnabled: false,
    creditDays: 30,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Customer[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const token = localStorage.getItem('accessToken');
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
  };

  // Validation helpers
  const validateForm = (values: typeof form): { isValid: boolean; errors: Record<string, string> } => {
    const errors: Record<string, string> = {};
    if (!values.name.trim()) errors.name = 'El nombre es requerido';
    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) errors.email = 'Email inválido';
    if (values.phone && !/^\+?[\d\s\-\(\)]+$/.test(values.phone)) errors.phone = 'Teléfono inválido';
    if (values.creditLimit && !/^\d+(\.\d{1,2})?$/.test(values.creditLimit)) errors.creditLimit = 'Límite de crédito inválido';
    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  // Load customers with debounced search
  useEffect(() => {
    const handler = setTimeout(() => loadCustomers(), 300);
    return () => clearTimeout(handler);
  }, [search]);

  async function loadCustomers() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/customers?q=${search}`, { headers });
      if (res.ok) {
        const d = await res.json();
        const list: Customer[] = d.data || [];
        setCustomers(list);
        // Update suggestions for autocomplete
        if (search.length >= 2) {
          setSuggestions(list);
          setShowSuggestions(true);
        } else {
          setShowSuggestions(false);
        }
      } else {
        const err = await res.json();
        throw new Error(err.error || 'Error al cargar clientes');
      }
    } catch (err: any) {
      setError(err.message ?? 'Error inesperado');
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setForm({
      name: '',
      taxId: '',
      email: '',
      phone: '',
      type: 'individual',
      address: '',
      creditLimit: '',
      creditEnabled: false,
      creditDays: 30,
    });
    setEditingId(null);
    setFormErrors({});
    setSaveMessage(null);
  }

  function startEdit(c: Customer) {
    setEditingId(c.id);
    setShowForm(true);
    setForm({
      name: c.name || '',
      taxId: c.taxId || '',
      email: c.email || '',
      phone: c.phone || '',
      type: c.type || 'individual',
      address: c.address || '',
      creditLimit: c.creditLimit?.toString() || '',
      creditEnabled: c.creditEnabled ?? false,
      creditDays: c.creditDays ?? 30,
    });
    setFormErrors({});
    setSaveMessage(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    const { isValid, errors } = validateForm(form);
    setFormErrors(errors);
    if (!isValid) {
      setSubmitting(false);
      return;
    }
    try {
      const body: Record<string, string | number | boolean | undefined> = {
        name: form.name,
        type: form.type,
        taxId: form.taxId || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        address: form.address || undefined,
        creditLimit: form.creditLimit ? parseFloat(form.creditLimit) : undefined,
        creditEnabled: form.creditEnabled,
        creditDays: form.creditLimit ? Number(form.creditDays) : undefined,
      };
      let res;
      if (editingId) {
        res = await fetch(`${API}/customers/${editingId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${API}/customers`, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });
      }
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al guardar');
      }
      setSaveMessage('Guardado con éxito');
      setTimeout(() => setSaveMessage(null), 3000);
      resetForm();
      loadCustomers();
    } catch (err: any) {
      setError(err.message ?? 'Error inesperado');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      // DELETE sin body: NO mandar Content-Type (fastify 400 si va JSON vacío)
      const delHeaders = { ...headers } as Record<string, string>;
      delete delHeaders['Content-Type'];
      const res = await fetch(`${API}/customers/${id}`, { method: 'DELETE', headers: delHeaders });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al eliminar');
      }
      setConfirmDelete(null);
      loadCustomers();
    } catch (err: any) {
      setError(err.message ?? 'Error inesperado');
    }
  }

  async function handleToggleActive(c: Customer) {
    try {
      const res = await fetch(`${API}/customers/${c.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ active: !c.active }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al cambiar estado');
      }
      loadCustomers();
    } catch (err: any) {
      setError(err.message ?? 'Error inesperado');
    }
  }

  // Handle keyboard shortcuts: Ctrl+Enter to submit when form is focused
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'Enter' && showForm && !submitting) {
        e.preventDefault();
        if (formRef.current) {
          formRef.current.requestSubmit();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showForm, submitting]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Clientes</h1>
        <button
          onClick={() => { resetForm(); setShowForm(!showForm); }}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium min-h-[44px] transition-colors"
        >
          {showForm && !editingId ? 'Cancelar' : '+ Nuevo Cliente'}
        </button>
      </div>

      {/* Error / Save messages */}
      {error && (
        <div className="bg-red-50 text-red-700 dark:text-red-300 p-4 rounded-lg mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="text-sm font-medium">⚠️ {error}</span>
          <button
            onClick={loadCustomers}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors min-h-[44px]"
          >
            Reintentar
          </button>
        </div>
      )}
      {saveMessage && (
        <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded text-center">
          {saveMessage}
        </div>
      )}

      {/* Search with autocomplete */}
      <div className="relative mb-4">
        <input
          ref={searchRef}
          placeholder="🔍 Buscar por nombre, CUIT, email o teléfono..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full px-4 py-2 mb-2 border rounded"
          aria-label="Buscar clientes"
        />
        {loading && <span className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></span>}
        {showSuggestions && (
          <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg">
            {suggestions.length === 0 ? (
              <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">No se encontraron resultados</div>
            ) : (
              suggestions.map(c => (
                <div
                  key={c.id}
                  onMouseDown={() => {
                    setSearch(`${c.name} ${c.taxId || ''}`.trim());
                    setShowSuggestions(false);
                  }}
                  className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2"
                >
                  <span className="flex-1">{c.name}</span>
                  {c.taxId && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 ml-2">{c.taxId}</span>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-6"
        >
          <h2 className="text-lg font-bold mb-4">
            {editingId ? '✏️ Editar Cliente' : '👤 Nuevo Cliente'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Nombre / Razón Social *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                required
              />
              {formErrors.name && (
                <p className="text-red-500 dark:text-red-400 text-sm mt-1">{formErrors.name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Tipo</label>
              <select
                value={form.type}
                onChange={e => setForm({ ...form, type: e.target.value })}
                className="w-full px-3 py-2 border rounded"
              >
                <option value="individual">Persona</option>
                <option value="business">Empresa</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">CUIT / DNI</label>
              <input
                type="text"
                value={form.taxId}
                onChange={e => setForm({ ...form, taxId: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                placeholder="30-12345678-9"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                placeholder="cliente@email.com"
              />
              {formErrors.email && (
                <p className="text-red-500 dark:text-red-400 text-sm mt-1">{formErrors.email}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Teléfono</label>
              <input
                type="text"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                placeholder="+54 11..."
              />
              {formErrors.phone && (
                <p className="text-red-500 dark:text-red-400 text-sm mt-1">{formErrors.phone}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Límite de Crédito</label>
              <input
                type="number"
                step="0.01"
                value={form.creditLimit}
                onChange={e => setForm({ ...form, creditLimit: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                placeholder="0.00"
              />
              {formErrors.creditLimit && (
                <p className="text-red-500 dark:text-red-400 text-sm mt-1">{formErrors.creditLimit}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer min-h-[44px]">
                <input
                  type="checkbox"
                  checked={form.creditEnabled}
                  onChange={e => setForm({ ...form, creditEnabled: e.target.checked })}
                  className="h-5 w-5 rounded border-gray-300 dark:border-gray-600"
                />
                Cuenta corriente habilitada
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Autorizado por el dueño del comercio. Permite vender en el POS a cuenta corriente.
              </p>
            </div>
            {form.creditEnabled && (
              <div>
                <label className="block text-sm font-medium mb-1">Plazo de pago (días)</label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={form.creditDays}
                  onChange={e => setForm({ ...form, creditDays: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="30"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Fecha esperada de pago = fecha de venta + este plazo.
                </p>
              </div>
            )}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Dirección</label>
              <input
                type="text"
                value={form.address}
                onChange={e => setForm({ ...form, address: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                placeholder="Calle, número, ciudad"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-600 text-white rounded-lg font-medium min-h-[44px] transition-colors disabled:opacity-70"
            >
              {submitting ? 'Guardando...' : editingId ? 'Guardar Cambios' : 'Crear Cliente'}
            </button>
            <button
              type="button"
              onClick={() => { resetForm(); setShowForm(false); }}
              disabled={submitting}
              className="px-6 py-2 bg-gray-300 hover:bg-gray-400 disabled:bg-gray-300 text-gray-700 dark:text-gray-200 rounded-lg font-medium min-h-[44px] transition-colors disabled:opacity-70"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Table / Mobile cards */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {/* Mobile cards */}
        <div className="block sm:hidden">
          {customers.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">
              {search ? 'No se encontraron clientes' : 'Hacé clic en "Nuevo Cliente" para crear el primero.'}
            </div>
          ) : (
            customers.map(c => (
              <div key={c.id} className={`p-4 border-b last:border-b-0 ${!c.active ? 'opacity-50' : ''}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <button
                      onClick={() => startEdit(c)}
                      className="font-semibold text-gray-800 dark:text-gray-100 text-left hover:text-blue-600 dark:hover:text-blue-400 min-h-[44px]"
                    >{c.name}</button>
                    <p className="text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{c.email || '-'} · {c.phone || '-'}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{c.taxId || '-'}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs h-fit ${c.type === 'business' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}>
                    {c.type === 'individual' ? 'Persona' : 'Empresa'}
                  </span>
                  {c.creditEnabled && (
                    <span className="px-2 py-1 rounded text-xs h-fit bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                      Cuenta corriente
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <button
                    onClick={() => handleToggleActive(c)}
                    className={`px-3 py-1.5 rounded text-xs font-medium min-h-[44px] min-w-[44px] ${c.active ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'}`}
                  >
                    {c.active ? 'Activo' : 'Inactivo'}
                  </button>
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(c)} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 text-sm font-medium px-3 py-1.5 min-h-[44px] min-w-[44px]">✏️ Editar</button>
                    {confirmDelete === c.id ? (
                      <>
                        <button onClick={() => handleDelete(c.id)} className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1.5 min-h-[44px]">✕ Confirmar</button>
                        <button onClick={() => setConfirmDelete(null)} className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm font-medium px-3 py-1.5 min-h-[44px]">Cancelar</button>
                      </>
                    ) : (
                      <button onClick={() => setConfirmDelete(c.id)} className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1.5 min-h-[44px] min-w-[44px]">🗑️ Eliminar</button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        {/* Desktop table */}
        <table className="hidden sm:table w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left">Nombre</th>
              <th className="px-4 py-3 text-left">Tipo</th>
              <th className="px-4 py-3 text-left">CUIT/DNI</th>
              <th className="px-4 py-3 text-left">Contacto</th>
              <th className="px-4 py-3 text-left">Teléfono</th>
              <th className="px-4 py-3 text-center">Estado</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {customers.map(c => (
              <tr key={c.id} className={`border-t ${!c.active ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3 font-medium">
                  <button
                    onClick={() => startEdit(c)}
                    className="text-left hover:text-blue-600 dark:hover:text-blue-400 min-h-[44px]"
                  >
                    {c.name}
                  </button>
                  {c.creditEnabled && (
                    <span className="ml-2 px-2 py-0.5 rounded text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">CC</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className={`px-2 py-1 rounded text-xs ${c.type === 'business' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}>
                    {c.type === 'individual' ? 'Persona' : 'Empresa'}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-sm">{c.taxId || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{c.email || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{c.phone || '-'}</td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => handleToggleActive(c)}
                    className={`px-3 py-1.5 rounded text-xs font-medium min-h-[44px] min-w-[44px] ${c.active ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'}`}
                  >
                    {c.active ? 'Activo' : 'Inactivo'}
                  </button>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => startEdit(c)} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 text-sm font-medium px-3 py-1.5 min-h-[44px] min-w-[44px]">✏️ Editar</button>
                  {confirmDelete === c.id ? (
                    <>
                      <button onClick={() => handleDelete(c.id)} className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1.5 min-h-[44px]">✕ Confirmar</button>
                      <button onClick={() => setConfirmDelete(null)} className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm font-medium px-3 py-1.5 min-h-[44px]">Cancelar</button>
                    </>
                  ) : (
                    <button onClick={() => setConfirmDelete(c.id)} className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1.5 min-h-[44px] min-w-[44px]">🗑️ Eliminar</button>
                  )}
                </td>
              </tr>
            ))}
            {customers.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">
                  {search ? 'No se encontraron clientes' : 'Hacé clic en "Nuevo Cliente" para crear el primero.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      {customers.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">
          {customers.length} cliente{customers.length !== 1 ? 's' : ''} •{' '}
          {customers.filter(c => c.active).length} activos •{' '}
          {customers.filter(c => !c.active).length} inactivos
        </div>
      )}
    </div>
  );
}